import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal, untracked } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { GlxyMarkdownPipe } from '@vendasta/galaxy/pipes';
import { PlanAction, SubmitPlanArgs } from './plan-display.types';

@Component({
  selector: 'ai-assistant-plan-display',

  imports: [MatButtonModule, MatFormFieldModule, MatIconModule, MatInputModule, GlxyMarkdownPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './plan-display.component.html',
  styleUrls: ['./plan-display.component.scss'],
})
export class PlanDisplayComponent {
  readonly plan = input.required<SubmitPlanArgs>();
  readonly action = input<PlanAction | undefined>(undefined);
  readonly approved = output<string>();
  readonly rejected = output<void>();

  private readonly localAction = signal<PlanAction | null>(null);
  protected readonly isEditing = signal(false);
  protected readonly draftPlan = signal('');
  private preEditSnapshot = '';

  protected readonly currentAction = computed<PlanAction | null>(() => {
    return this.action() ?? this.localAction();
  });

  constructor() {
    effect(() => {
      const next = this.plan().plan;
      const editing = untracked(() => this.isEditing());
      if (editing) return;
      this.draftPlan.set(next);
      this.preEditSnapshot = next;
    });
  }

  protected onApprove(): void {
    if (this.currentAction() !== null) return;
    this.localAction.set('approved');
    this.isEditing.set(false);
    this.approved.emit(this.draftPlan());
  }

  protected onReject(): void {
    if (this.currentAction() !== null) return;
    this.localAction.set('rejected');
    this.rejected.emit();
  }

  protected onEdit(): void {
    if (this.currentAction() !== null) return;
    this.preEditSnapshot = this.draftPlan();
    this.isEditing.set(true);
  }

  protected onCancelEditing(): void {
    this.draftPlan.set(this.preEditSnapshot);
    this.isEditing.set(false);
  }

  protected onDraftInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.draftPlan.set(target.value);
  }
}
